import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, UserMinus, UserPlus } from 'lucide-react';
import { format } from 'date-fns';
import { useDepartment, useUpdateDepartment, useAddMember, useRemoveMember } from '@/hooks/useDepartments';
import DepartmentActivityTab from './DepartmentActivityTab';

const StatusBadge = ({ status }) =>
  status === 'active' ? (
    <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-0">Active</Badge>
  ) : (
    <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 border-0">Archived</Badge>
  );




const DepartmentDetailsDrawer = ({ open, onClose, departmentId, companyUsers = [] }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [notes, setNotes] = useState('');
  const [notesEdited, setNotesEdited] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState('');

  const { data: department, isLoading } = useDepartment(departmentId);
  const updateMutation = useUpdateDepartment();
  const addMemberMutation = useAddMember();
  const removeMemberMutation = useRemoveMember();

  // Sync notes textarea when department loads
  const currentNotes = department?.notes || '';
  if (!notesEdited && notes !== currentNotes) {
    setNotes(currentNotes);
  }

  const handleNotesChange = (val) => {
    setNotes(val);
    setNotesEdited(true);
  };

  const handleSaveNotes = () => {
    updateMutation.mutate(
      { id: departmentId, data: { notes } },
      { onSuccess: () => setNotesEdited(false) }
    );
  };

  const handleAddMember = () => {
    if (!selectedMemberId) return;
    addMemberMutation.mutate(
      { id: departmentId, userId: selectedMemberId },
      { onSuccess: () => setSelectedMemberId('') }
    );
  };

  const handleRemoveMember = (memberId) => {
    removeMemberMutation.mutate({ id: departmentId, memberId });
  };

  // Users not already members and not the owner
  const existingMemberIds = (department?.members || []).map((m) => m._id || m);
  const ownerId = department?.owner?._id || department?.owner;
  const availableToAdd = companyUsers.filter(
    (u) => !existingMemberIds.includes(u._id) && u._id !== ownerId
  );

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {department?.color && (
                  <span
                    className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: department.color }}
                  />
                )}
                {department?.name || 'Department'}
              </>
            )}
          </SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading...
          </div>
        ) : !department ? (
          <div className="text-center py-20 text-gray-400 text-sm">Department not found</div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full mb-4">
              <TabsTrigger value="summary" className="flex-1">Summary</TabsTrigger>
              <TabsTrigger value="members" className="flex-1">
                Members ({department.members?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="activities" className="flex-1">Activities</TabsTrigger>
              <TabsTrigger value="notes" className="flex-1">Notes</TabsTrigger>
            </TabsList>

            {/* ── Summary ── */}
            <TabsContent value="summary" className="space-y-4">
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Status</span>
                  <StatusBadge status={department.status} />
                </div>

                {department.code && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Code</span>
                    <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                      {department.code}
                    </span>
                  </div>
                )}

                {department.owner && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Owner</span>
                    <span>{department.owner.name || department.owner.email || '—'}</span>
                  </div>
                )}

                {department.color && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Color</span>
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-4 h-4 rounded-full border"
                        style={{ backgroundColor: department.color }}
                      />
                      <span className="text-xs font-mono">{department.color}</span>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Created</span>
                  <span>
                    {department.createdAt
                      ? format(new Date(department.createdAt), 'MMM dd, yyyy')
                      : '—'}
                  </span>
                </div>

                {department.description && (
                  <div>
                    <p className="text-gray-500 mb-1">Description</p>
                    <p className="text-gray-700 dark:text-gray-300">{department.description}</p>
                  </div>
                )}

                {department.tags?.length > 0 && (
                  <div>
                    <p className="text-gray-500 mb-1">Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {department.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ── Members ── */}
            <TabsContent value="members" className="space-y-4">
              {/* Add member */}
              {department.status === 'active' && availableToAdd.length > 0 && (
                <div className="flex gap-2">
                  <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
                    <SelectTrigger className="flex-1 h-8 text-sm">
                      <SelectValue placeholder="Select user to add" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableToAdd.map((u) => (
                        <SelectItem key={u._id} value={u._id}>
                          {u.name} — {u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={handleAddMember}
                    disabled={!selectedMemberId || addMemberMutation.isPending}
                    className="h-8"
                  >
                    {addMemberMutation.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <UserPlus className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              )}

              {/* Member list */}
              {department.members?.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No members yet</p>
              ) : (
                <div className="space-y-2">
                  {department.members.map((member) => {
                    const memberId = member._id || member;
                    const memberName = member.name || memberId;
                    const memberEmail = member.email || '';
                    return (
                      <div key={memberId} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                        <div>
                          <p className="font-medium">{memberName}</p>
                          {memberEmail && <p className="text-xs text-gray-400">{memberEmail}</p>}
                        </div>
                        {department.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(memberId)}
                            disabled={removeMemberMutation.isPending}
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <UserMinus className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* ── Activities ── */}
            <TabsContent value="activities">
              <DepartmentActivityTab departmentId={departmentId} />
            </TabsContent>

            {/* ── Notes ── */}
            <TabsContent value="notes" className="space-y-3">
              <Textarea
                value={notes}
                onChange={(e) => handleNotesChange(e.target.value)}
                placeholder="Add internal notes about this department..."
                rows={8}
                className="resize-none"
              />
              <Button
                onClick={handleSaveNotes}
                disabled={!notesEdited || updateMutation.isPending}
                size="sm"
              >
                {updateMutation.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                Save Notes
              </Button>
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default DepartmentDetailsDrawer;
